import React, { useState } from 'react';
import { useServiceKey } from '../hooks/useServiceKey';
import { Phone, Mail, Info } from 'lucide-react';
import QrCodePanel from './QrCodePanel'; // Import the new QrCodePanel component
import { useAuth } from '../hooks/useAuth'; // Import useAuth
import { trackUserInteraction } from '../services/analytics'; // Import analytics tracking
import PWAInstallButton from '../PWA/PWAInstallButton'; // Import PWA install button
import Badge from './ui/Badge'; // Import the Badge component
import SectionHeader from './ui/SectionHeader'; // Import SectionHeader component

const ServiceInfo: React.FC = () => {
  const { serviceKey, serviceConfig, attributes } = useServiceKey();
  const { currentUser } = useAuth(); // Get currentUser
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Dynamically generate the app link based on the website root and service key
  const root = typeof window !== 'undefined' ? window.location.origin : '';
  let appLink = serviceKey && serviceKey !== 'default' ? `${root}/${serviceKey}` : root;
  if (currentUser?.id) {
    appLink += `?source=${currentUser.id}`;
  }
  // Determine primary color from serviceConfig, fallback to a default
  const primaryColor = (serviceConfig as any)?.colors?.primary || serviceConfig?.config?.primaryColor || '#007bff';

  // Track when users interact with contact information
  const trackContactClick = (method: string, value: string) => {
    trackUserInteraction('UI:Contact Info Click', {
      method,
      value,
      serviceKey
    });
  };
  // Track when QR code is viewed or shared
  const trackQrCodeInteraction = (action: string) => {
    trackUserInteraction('UI:QR Code Interaction', {
      action,
      serviceKey
    });
  };

  return (
    <div className="card-container">
      <SectionHeader
        title="Service Information"
        icon={Info}
        collapsible={true}
        collapsed={isCollapsed}
        onCollapsedChange={setIsCollapsed}
        rightContent={
          <Badge variant="info">
            serviceKey: {serviceKey}
          </Badge>
        }
      >
        {/* Three-column layout: On mobile, first two columns stay side by side, third stacks below */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Merge columns 1 and 2, then use a 2x4 grid inside */}
          <div className="col-span-1 lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 grid-rows-4 gap-x-6 gap-y-2">
              {/* Row 1: Timezone | Max Distance */}
              {serviceConfig.timezone && (
                <div className="row-span-1 col-span-1 text-sm text-foreground-muted">
                  <span className="font-medium text-foreground">Timezone:</span> {serviceConfig.timezone}
                </div>
              )}
              {serviceConfig.maxDistance && (
                <div className="row-span-1 col-span-1 text-sm text-foreground-muted">
                  <span className="font-medium text-foreground">Max Distance:</span> {serviceConfig.maxDistance} km
                </div>
              )}
              {/* Row 2: Region | (empty) */}
              <div className="row-span-1 col-span-1 text-sm text-foreground-muted">
                <span className="font-medium text-foreground">Region:</span> {attributes.region || 'Global'}
              </div>
              <div className="row-span-1 col-span-1"></div>
              {/* Row 3: Phone (col-span-2) */}
              {serviceConfig.contactInfo && serviceConfig.contactInfo.phone && (
                <div className="row-span-1 col-span-2 flex items-center text-sm text-foreground-muted mt-1">
                  <span className="font-medium text-foreground w-16 shrink-0">Phone:</span>
                  <a
                    href={`tel:${serviceConfig.contactInfo.phone}`}
                    className="ml-1 text-primary hover:underline inline-flex items-center"
                    title="Call"
                    onClick={() => trackContactClick('Phone', serviceConfig.contactInfo.phone)}
                  >
                    {serviceConfig.contactInfo.phone}
                    <Phone className="ml-2 w-4 h-4 text-primary/80" aria-hidden="true" />
                  </a>
                </div>
              )}
              {/* Row 4: Available Vehicles (col-span-2) */}
              {attributes.vehicleTypes && Array.isArray(attributes.vehicleTypes) && attributes.vehicleTypes.length > 0 && (
                <div className="row-span-1 col-span-2 text-sm text-foreground-muted mt-1">
                  <span className="font-medium text-foreground">Available Vehicles:</span> {attributes.vehicleTypes.join(', ')}
                </div>
              )}
              {/* Row 4: Email (col-span-2, after vehicles if present) */}
              {serviceConfig.contactInfo && serviceConfig.contactInfo.email && (
                <div className="row-span-1 col-span-2 flex items-center text-sm text-foreground-muted mt-1">
                  <span className="font-medium text-foreground w-16 shrink-0">Email:</span>
                  <a
                    href={`mailto:${serviceConfig.contactInfo.email}`}
                    className="ml-1 text-primary hover:underline inline-flex items-center break-all"
                    title="Email"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackContactClick('Email', serviceConfig.contactInfo.email)}
                  >
                    {serviceConfig.contactInfo.email}
                    <Mail className="ml-2 w-4 h-4 text-primary/80 flex-shrink-0" aria-hidden="true" />
                  </a>
                </div>
              )}
            </div>
          </div>
          {/* Right Column: QR Code and PWA Install, stacks below on mobile */}
          <div className="flex flex-col justify-center items-center h-full pt-4 md:pt-0 col-span-1 space-y-4">
            <QrCodePanel appLink={appLink} primaryColor={primaryColor} onQrCodeInteraction={trackQrCodeInteraction} />
            <PWAInstallButton
              primaryColor={primaryColor}
              className="compact"
              serviceKey={serviceKey}
              serviceName={serviceConfig.name || 'this service'}
            />
          </div>
        </div>
      </SectionHeader>
    </div>
  );
};

export default ServiceInfo;
